import { Button, Modal } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AppContext } from '../../../../../../../AppContext';
import history from '../../../../../../../historyData';

export default function UnsavedChangesPrompt(props) {
  const {
    openPrompt = false,
    onSaveDraft,
    title = 'Leave this page',
    description = 'You have unsaved changes. Would you like to save them as a draft before leaving?',
    saveText = 'Save as Draft',
    discardText = 'Discard',
  } = props;

  const { dispatch } = useContext(AppContext);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [blockedLocation, setBlockedLocation] = useState(null);
  const [confirmedLocation, setConfirmedLocation] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (
        !confirmedLocation &&
        openPrompt &&
        nextLocation.pathname !== location?.pathname
      ) {
        setShowPrompt(true);
        setBlockedLocation(nextLocation);
        return true;
      }
      return false;
    },
    [confirmedLocation, openPrompt, location],
  );

  const handleSave = async () => {
    setIsSaving(true);
    if (onSaveDraft) {
      try {
        const res = await onSaveDraft();
        if (!res?.data) {
          setIsSaving(false);
          return;
        }
      } catch {
        setIsSaving(false);
        return;
      }
    }
    setIsSaving(false);
    setShowPrompt(false);
    dispatch({ type: 'SET_SHOW_PROMPT', data: false });
    setConfirmedLocation(blockedLocation);
  };

  const handleDiscard = () => {
    if (!isSaving) {
      setShowPrompt(false);
      dispatch({ type: 'SET_SHOW_PROMPT', data: false });
      setConfirmedLocation(blockedLocation);
    }
  };

  const handleCancel = () => {
    if (!isSaving) {
      setShowPrompt(false);
      setBlockedLocation(null);
    }
  };

  useEffect(() => {
    if (confirmedLocation) {
      navigate(confirmedLocation?.pathname, {
        state: confirmedLocation?.state,
      });
    }
  }, [confirmedLocation, navigate]);

  useEffect(() => {
    setConfirmedLocation(null);
  }, [location]);

  useEffect(() => {
    if (openPrompt) {
      // eslint-disable-next-line no-undef
      window.onbeforeunload = (event) => {
        // eslint-disable-next-line no-undef
        const e = event || window?.event;
        e?.preventDefault();
        if (e) {
          e.returnValue = description;
        }
        return description;
      };
    } else {
      // eslint-disable-next-line no-undef
      window.onbeforeunload = null;
    }

    return () => {
      // eslint-disable-next-line no-undef
      window.onbeforeunload = null;
    };
  }, [openPrompt]);

  useEffect(() => {
    if (!openPrompt) return;
    const unblock = history?.block((tx) => {
      if (handleBlockedNavigation(tx?.location)) {
        return;
      }
      tx?.retry();
    });
    return () => {
      unblock();
    };
  }, [handleBlockedNavigation, openPrompt]);

  return (
    <Modal
      open={showPrompt}
      title={title}
      closable
      onCancel={handleCancel}
      maskClosable={false}
      centered
      footer={[
        <Button type="link" shape="round" key="discard" onClick={handleDiscard}>
          {discardText}
        </Button>,
        <Button
          key="save"
          shape="round"
          type="primary"
          loading={isSaving}
          onClick={handleSave}
          className="save-button"
        >
          {saveText}
        </Button>,
      ]}
    >
      <p>{description}</p>
    </Modal>
  );
}
